import { safeGetLocalStorageItem } from '@mc/browser-storage'
import { saveMultipleUserData } from '../saveMultipleUserData'
import { saveUserData } from '../saveUserData'
import {
  ActionTypes,
  canSaveUserData,
  KeyType,
  SaveUserDataProps,
  SaveMultipleUserDataProps,
  SavedUserData,
} from '../utils'

type IdsType = string | string[] | number | number[]

export class BaseAccessor {
  private readonly keyType: KeyType

  constructor(keyType: KeyType) {
    this.keyType = keyType
  }

  private isUserDataAllowed() {
    return canSaveUserData()
  }

  private setData(data: Omit<SaveUserDataProps, 'key'>) {
    if (!this.isUserDataAllowed()) return

    saveUserData({ ...data, key: this.keyType })
  }

  private setMultipleData(data: Omit<SaveMultipleUserDataProps, 'key'>) {
    if (!this.isUserDataAllowed()) return

    saveMultipleUserData({ ...data, key: this.keyType })
  }

  private getData() {
    if (!this.isUserDataAllowed()) {
      return undefined
    }

    return JSON.parse(
      safeGetLocalStorageItem(this.keyType) || '{}',
    ) as SavedUserData
  }

  private getIdFromKey(key: string): number {
    return Number.parseInt(key.split('-')[1], 10)
  }

  private filterValidIds(value: number): boolean {
    return !!value && !Number.isNaN(value)
  }

  private castIdsToString(ids: IdsType): string[] | string {
    if (Array.isArray(ids)) {
      return ids.map(String)
    }

    return String(ids)
  }

  public setDeclaredInterestIds(rawIds: IdsType, eventName: string): void {
    const ids = this.castIdsToString(rawIds)

    if (Array.isArray(ids)) {
      this.setMultipleData({
        ids: ids.map(String),
        actionType: ActionTypes.DECLARED,
        eventName,
      })
    } else {
      this.setData({
        id: ids.toString(),
        actionType: ActionTypes.DECLARED,
        eventName,
      })
    }
  }

  public setBrowsedInterestIds(rawIds: IdsType, eventName: string): void {
    const ids = this.castIdsToString(rawIds)

    if (Array.isArray(ids)) {
      this.setMultipleData({ ids, actionType: ActionTypes.BROWSING, eventName })
    } else {
      this.setData({ id: ids, actionType: ActionTypes.BROWSING, eventName })
    }
  }

  public getAllInterestIds(): number[] {
    const data = this.getData()
    if (!data) return []
    return Object.keys(data).map(this.getIdFromKey).filter(this.filterValidIds)
  }

  public getBrowsedInterestIds(): number[] {
    const data = this.getData()
    if (!data) return []
    return Object.entries(data)
      .filter(([_, value]) => value?.[ActionTypes.BROWSING]?.overallCount > 0)
      .map(([key]) => this.getIdFromKey(key))
      .filter(this.filterValidIds)
  }

  public getDeclaredInterestIds(): number[] {
    const data = this.getData()
    if (!data) return []
    return Object.entries(data)
      .filter(([_, value]) => value?.[ActionTypes.DECLARED]?.overallCount > 0)
      .map(([key]) => this.getIdFromKey(key))
      .filter(this.filterValidIds)
  }

  public getSortedIdsByCount(): number[] {
    const data = this.getData()
    if (!data) return []
    return Object.entries(data)
      .map(([key, value]) => ({
        id: this.getIdFromKey(key),
        count:
          (value?.[ActionTypes.BROWSING]?.overallCount || 0) +
          (value?.[ActionTypes.DECLARED]?.overallCount || 0),
      }))
      .sort((a, b) => b.count - a.count)
      .map(({ id }) => id)
      .filter(this.filterValidIds)
  }

  public getInterestIdsByEventNames(eventNames: string[]): number[] {
    const data = this.getData()
    if (!data) return []
    return Object.entries(data)
      .filter(([_, value]) =>
        eventNames.some(
          (eventName) =>
            value?.browsing?.[eventName]?.count > 0 ||
            value?.declared?.[eventName]?.count > 0,
        ),
      )
      .map(([key]) => this.getIdFromKey(key))
  }

  public hasInterests(): boolean {
    return this.getAllInterestIds().length > 0
  }
}
